import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Image, Minus, Plus, Search } from 'react-feather';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Row } from 'react-table';
import ConfirmModal from '../components/confirm-modal';
import NoteModal from '../components/note-modal';
import Table from '../components/table';
import { Role } from '../enums/role';
import { TransferStatus } from '../enums/transfer-status';
import { Category } from '../interfaces/category';
import { DraftProductTransferDto } from '../interfaces/draft-product-transfer.dto';
import { GenerateLabelsDto } from '../interfaces/generate-labels.dto';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Organization } from '../interfaces/organization';
import { ProductTransfer } from '../interfaces/product-transfer';
import { ProductTransferDto } from '../interfaces/product-transfer.dto';
import { Transfer } from '../interfaces/transfer';
import { User } from '../interfaces/user';
import { api } from '../services/api';
import { downloadPdf } from '../services/helpers';
import myToastr from '../services/toastr';
import { useAppSelector } from '../store/hooks';
import { RootState } from '../store/store';

enum RowStatus {
  All = 'all',
  Filled = 'filled',
  Empty = 'empty',
}

const VerifyTransferView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const user: User = useAppSelector((state: RootState) => state.auth.user!);
  const organization: Organization = useAppSelector((state: RootState) => state.auth.organization!);
  const [showVerifyTransferModal, setShowVerifyTransferModal] = useState<boolean>(false);
  const [showDeleteTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<number>(-1);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(-1);
  const [selectedStatus, setSelectedStatus] = useState<RowStatus>(RowStatus.All);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [filteredProductTransfers, setFilteredProductTransfers] = useState<ProductTransfer[]>([]);
  const [units, setUnits] = useState<{ [productId: number]: number }>({});
  const [requesting, setRequesting] = useState<boolean>(false);
  const [generatingLabels, setGeneratingLabels] = useState<boolean>(false);
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<Row<ProductTransfer>[]>([]);
  const receivedUnitsRef = useRef<number | null>(null);
  const brands: { id: number; name: string }[] = useMemo(() => {
    let brs: { id: number; name: string }[] = [];
    if (transfer) {
      transfer.transferredProducts.forEach((productTransfer: ProductTransfer) => {
        const index: number = brs.findIndex((b: { id: number; name: string }) => b.id === productTransfer.product.brand.id);
        if (index === -1) {
          brs.push({ id: productTransfer.product.brand.id, name: productTransfer.product.brand.name });
        }
      });
    }
    return brs;
  }, [transfer]);
  const categories: { id: number; name: string }[] = useMemo(() => {
    let crs: { id: number; name: string }[] = [];
    if (transfer) {
      transfer.transferredProducts.forEach((productTransfer: ProductTransfer) => {
        productTransfer.product.categories.forEach((category: Category) => {
          const index: number = crs.findIndex((c: { id: number; name: string }) => c.id === category.id);
          if (index === -1) {
            crs.push({ id: category.id, name: category.name });
          }
        });
      });
    }
    return crs;
  }, [transfer]);

  useEffect(() => {
    if (transfer) {
      const u: { [productId: number]: number } = {};
      transfer.transferredProducts.forEach((productTransfer: ProductTransfer) => {
        if (productTransfer.unitsReceived !== null) {
          u[productTransfer.productId] = productTransfer.unitsReceived;
        }
      });
      setUnits(u);
    }
  }, [transfer]);

  useEffect(() => {
    if (params?.id) {
      const getTransfer = async () => {
        try {
          const t: Transfer = await api.getTransferToVerify(parseInt(params.id!, 10));
          if (t.status !== TransferStatus.Pending) {
            navigate(`/traspaso/${t.id}`);
            return;
          }
          setTransfer(t);
        } catch (e: any) {
          myToastr.error(e.response.data.message);
          navigate('/traspasos', { replace: true });
        }
      };
      getTransfer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!transfer) {
      return;
    }
    let filteredStock: ProductTransfer[] = [...transfer.transferredProducts];
    if (searchText.length > 0) {
      filteredStock = filteredStock.filter((s: ProductTransfer) => {
        return (
          s.product.name.toLowerCase().includes(searchText.toLowerCase()) ||
          s.product.sku.toLowerCase().includes(searchText.toLowerCase()) ||
          (s.product.ean && s.product.ean.toLowerCase().includes(searchText.toLowerCase()))
        );
      });
    }
    // Marca
    if (selectedBrandId > 0) {
      filteredStock = filteredStock.filter((s: ProductTransfer) => s.product.brandId === selectedBrandId);
    }
    // Categoría
    if (selectedCategoryId > 0) {
      filteredStock = filteredStock.filter((s: ProductTransfer) => {
        const category = s.product.categories.find((c: Category) => c.id === selectedCategoryId);
        return category !== undefined;
      });
    }
    // Estado
    if (selectedStatus === RowStatus.Filled) {
      filteredStock = filteredStock.filter((s: ProductTransfer) => units.hasOwnProperty(s.productId));
    } else if (selectedStatus === RowStatus.Empty) {
      filteredStock = filteredStock.filter((s: ProductTransfer) => !units.hasOwnProperty(s.productId));
    }
    setFilteredProductTransfers(filteredStock);
  }, [searchText, selectedBrandId, selectedCategoryId, selectedStatus, units, transfer]);

  const updateUnits = useCallback(
    (productId: number, value: number | null) => {
      const u: { [productId: number]: number } = { ...units };
      if (value === null) {
        delete u[productId];
      } else {
        u[productId] = value;
      }
      setUnits({ ...u });
    },
    [units],
  );

  const incrementUnits = useCallback(
    (productId: number) => {
      const u: { [productId: number]: number } = { ...units };
      if (u.hasOwnProperty(productId)) {
        u[productId] = u[productId] + 1;
      } else {
        u[productId] = 1;
      }
      setUnits({ ...u });
    },
    [units],
  );

  const decrementUnits = useCallback(
    (productId: number) => {
      const u: { [productId: number]: number } = { ...units };
      if (u.hasOwnProperty(productId)) {
        u[productId] = u[productId] - 1;
        if (u[productId] < 0) {
          delete u[productId];
        }
      }
      setUnits({ ...u });
    },
    [units],
  );

  const deleteTransfer = async () => {
    try {
      setRequesting(true);
      await api.deleteTransfer(transfer!.id);
      myToastr.info('Traspaso eliminado');
      navigate('/traspasos', { replace: true });
    } catch (e: any) {
      myToastr.error(e.response.data.message);
      setRequesting(false);
    }
  };

  const draftVerifyTransfer = async () => {
    try {
      setRequesting(true);
      const draftProductTransferDtos: DraftProductTransferDto[] = [];
      for (const productId in units) {
        if (units.hasOwnProperty(productId)) {
          draftProductTransferDtos.push({ productId: parseInt(productId, 10), quantity: units[productId] });
        }
      }
      const t = await api.draftVerifyTransfer(transfer!.id, draftProductTransferDtos);
      setTransfer(t);
      myToastr.info('Traspaso guardado');
    } catch (e: any) {
      myToastr.error(e.response.data.message);
    } finally {
      setRequesting(false);
    }
  };

  const verifyTransfer = async () => {
    try {
      setRequesting(true);
      const transferredProductDtos: ProductTransferDto[] = [];
      for (const productId in units) {
        if (units.hasOwnProperty(productId)) {
          transferredProductDtos.push({ productId: parseInt(productId, 10), quantity: units[productId] });
        }
      }
      await api.verifyTransfer(transfer!.id, transferredProductDtos);
      navigate('/traspasos', { replace: true });
    } catch (e: any) {
      myToastr.error(e.response.data.message);
      setRequesting(false);
    }
  };

  const getProductsLabels = async () => {
    if (generatingLabels) {
      return;
    }
    try {
      myToastr.info('Generando etiquetas. Este proceso puede llevar segundos. Espere por favor...');
      setGeneratingLabels(true);
      const generateLabelsDto: GenerateLabelsDto = {
        storeIds: [transfer!.fromStoreId],
        products: transfer!.transferredProducts.map((productTransfer: ProductTransfer) => {
          return {
            productId: productTransfer.productId,
            quantity: productTransfer.unitsShipped,
          };
        }),
      };
      const result = await api.getProductsLabels(generateLabelsDto);
      downloadPdf(result, `etiquetas-traspaso-${transfer!.internalId}.pdf`);
    } catch (e) {
      myToastr.error('Hubo un error obteniendo la etiqueta del producto');
    } finally {
      setGeneratingLabels(false);
    }
  };

  const onCloseNoteModal = () => {
    setShowNoteModal(false);
  };

  const onSaveNotes = async (notes: string | null) => {
    try {
      const t: Transfer = await api.updateTransferNotes(transfer!.id, notes);
      setTransfer(t);
      setShowNoteModal(false);
      myToastr.info('Notas guardadas');
    } catch (e: any) {
      const httpExceptionDto: HttpExceptionDto = e.response.data;
      myToastr.error(httpExceptionDto.message);
    }
  };

  const exportTransfer = async () => {
    if (requesting) {
      return;
    }
    setRequesting(true);
    try {
      myToastr.info('Generando el documento. Espere por favor.');
      const result = await api.exportTransfer(transfer!.id, {
        ids: transfer!.transferredProducts.length === filteredRows.length ? [] : filteredRows.map((row) => row.original.id),
        columns: ['productName', 'sku', 'brand', 'categories', 'unitsSent'],
      });
      downloadPdf(result, `traspaso-${transfer!.internalId}.pdf`);
    } catch (e) {
      myToastr.error('Hubo un error obteniendo el ticket del arqueo');
    } finally {
      setRequesting(false);
    }
  };

  const columns = useMemo(() => {
    const columnsData: any[] = [
      {
        Header: 'Producto',
        accessor: 'product.name',
        className: 'text-start',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return (
            <div className="d-flex align-items-center container-product">
              {product.product.images.length > 0 ? (
                <>
                  <div style={{ cursor: 'zoom-in' }} onMouseEnter={() => setImageUrl(product.product.images[0].path)} onMouseLeave={() => setImageUrl(null)}>
                    <Search className="me-3" size={16} color="#808A95" />
                  </div>
                  <LazyLoadImage className="me-3" src={process.env.REACT_APP_PUBLIC_URL + product.product.images[0].path} alt={product.product.name} />
                </>
              ) : (
                <Image className="me-3" size={26} color="#808A95" />
              )}
              <span>{product.product.name}</span>
            </div>
          );
        },
      },
      {
        Header: 'SKU',
        accessor: 'product.sku',
        className: 'text-center',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return <span>{product.product.sku}</span>;
        },
      },
      {
        Header: 'Marca',
        accessor: 'product.brand.name',
        className: 'text-center',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return <span>{product.product.brand.name}</span>;
        },
      },
      {
        Header: 'Categorías',
        accessor: 'product.categories',
        className: 'text-center',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return <span>{product.product.categories.map((c: Category) => c.name).join(', ')}</span>;
        },
      },
      ...(user.role === Role.SuperAdmin || user.role === Role.Admin || user.role === Role.Manager
        ? [
            {
              Header: 'Uds. Enviadas',
              accessor: 'unitsShipped',
              className: 'text-center',
              disableFilters: true,
              Cell: ({ row }: any) => {
                const product: ProductTransfer = row.original;
                return <span>{product.unitsShipped}</span>;
              },
            },
          ]
        : []),
      {
        Header: 'Uds. Recibidas',
        accessor: 'receivedUnits',
        className: 'text-center',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return (
            <div className="d-flex align-items-center justify-content-center container-units">
              <Minus
                onClick={() => {
                  decrementUnits(product.productId);
                  receivedUnitsRef.current = product.id;
                }}
                className="clickable me-2"
                size={14}
                color="#535b66"
              />
              <input
                className="fw-bold"
                type="number"
                step={1}
                min={0}
                value={units.hasOwnProperty(product.productId) ? units[product.productId] : ''}
                onChange={(e: any) => {
                  updateUnits(product.productId, e?.target?.value && e.target.value.length > 0 ? parseInt(e.target.value, 10) : null);
                  receivedUnitsRef.current = product.id;
                }}
                onBlur={() => (receivedUnitsRef.current = null)}
                autoFocus={receivedUnitsRef.current === product.id}
              />
              <Plus
                onClick={() => {
                  incrementUnits(product.productId);
                  receivedUnitsRef.current = product.id;
                }}
                className="clickable ms-2"
                size={14}
                color="#535b66"
              />
            </div>
          );
        },
      },
      {
        Header: 'Estado',
        accessor: 'status',
        className: 'text-center',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const product: ProductTransfer = row.original;
          return (
            <div className="d-flex justify-content-center">
              {units.hasOwnProperty(product.productId) ? (
                <div className="transfer-product-status-icon filled-icon" title="Verificado"></div>
              ) : (
                <div className="transfer-product-status-icon empty-icon" title="Por verificar"></div>
              )}
            </div>
          );
        },
      },
    ];
    return columnsData;
  }, [decrementUnits, incrementUnits, units, updateUnits, user.role]);

  if (!transfer) {
    return null;
  }

  const pendingUnitsToFill = Math.max(transfer.transferredProducts.length - Object.keys(units).length, 0);
  let labelVerifyButton = 'Verificar traspaso';
  if (pendingUnitsToFill === 1) {
    labelVerifyButton = 'Verificar traspaso (1 producto pendiente)';
  } else if (pendingUnitsToFill > 1) {
    labelVerifyButton = `Verificar traspaso (${pendingUnitsToFill} productos pendientes)`;
  }

  return (
    <div className="verify-transfer-view position-relative p-4">
      <div className="d-flex flex-column h-100">
        <div className="d-flex flex-row align-items-center mb-2">
          <Link to="/traspasos" className="d-flex flex-row align-items-center come-back me-4">
            <ChevronLeft className="me-1" size={14} />
            <span>Volver</span>
          </Link>
          <div className="flex flex-column flex-grow-1">
            <h1 className="title">Verificar traspaso {transfer.internalId}</h1>
            <div className="flex flex-row subtitle">
              <span>
                Tienda origen: <strong>{transfer.fromStore.name}</strong>
              </span>
              <span className="ms-4">
                Tienda destino: <strong>{transfer.toStore.name}</strong>
              </span>
            </div>
          </div>
          <div
            onClick={() => setShowNoteModal(true)}
            className={clsx('d-flex align-items-center button-header me-3', { 'has-notes': transfer.notes != null && transfer.notes !== '' })}
            style={{ fontSize: '14px', padding: '7px 10px' }}
          >
            {transfer.notes ? (
              '1 Nota'
            ) : (
              <React.Fragment>
                <Plus className="me-1" size={14} />
                <span>Nota</span>
              </React.Fragment>
            )}
          </div>
          <button className="create-button create-expiration me-2" disabled={generatingLabels} onClick={getProductsLabels}>
            Descargar etiquetas
          </button>
          <button className="create-button create-expiration me-2" disabled={requesting} onClick={exportTransfer}>
            Imprimir
          </button>
        </div>
        <div className="container-filters d-flex flex-row my-2">
          <input type="text" placeholder="Buscar" className="me-1" onChange={(e: any) => setSearchText(e.target.value)} />
          <select name="brand" className="mx-1" onChange={(e: any) => setSelectedBrandId(parseInt(e.target.value, 10))}>
            <option value={-1}>Todas las Marca</option>
            {brands.map((b: { id: number; name: string }) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select name="category" className="mx-1" onChange={(e: any) => setSelectedCategoryId(parseInt(e.target.value, 10))}>
            <option value={-1}>Todas las Categorías</option>
            {categories.map((c: { id: number; name: string }) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select name="status" className="mx-1" onChange={(e: any) => setSelectedStatus(e.target.value as RowStatus)}>
            <option value={RowStatus.All}>Todos los estados</option>
            <option value={RowStatus.Filled}>Inventariado</option>
            <option value={RowStatus.Empty}>No inventariado</option>
          </select>
        </div>
        <div className="container-table my-2">
          <Table
            columns={columns}
            data={filteredProductTransfers}
            initialState={{
              sortBy: [{ id: 'product.sku', desc: false }],
            }}
            noDataMessage="No hay productos disponibles con los filtros seleccionados"
            onFilteredRowsChanged={setFilteredRows}
          />
        </div>
        <div className="mt-auto container-buttons">
          <div className="d-flex justify-content-end gap-2">
            {(user.role === Role.SuperAdmin || user.role === Role.Admin || user.role === Role.Manager) && (
              <button disabled={requesting} onClick={() => setShowTransferModal(true)} className="delete-button">
                Eliminar
              </button>
            )}
            <button disabled={requesting} onClick={() => draftVerifyTransfer()} className="save-draft-button">
              Guardar borrador
            </button>
            <button disabled={requesting || pendingUnitsToFill > 0} onClick={() => setShowVerifyTransferModal(true)} className="finalize-button">
              {labelVerifyButton}
            </button>
          </div>
        </div>
      </div>
      {imageUrl != null && (
        <div className="position-absolute top-50 start-50 translate-middle preview-image">
          <div className="d-flex justify-content-center">
            <LazyLoadImage src={process.env.REACT_APP_PUBLIC_URL + imageUrl} alt="" />
          </div>
        </div>
      )}
      <ConfirmModal
        acceptButtonClass="accept-button"
        show={showVerifyTransferModal}
        title="Verificar Traspaso"
        content="¿Estás seguro que quieres verificar el traspaso?"
        closeModal={(result: boolean) => {
          setShowVerifyTransferModal(false);
          if (result) {
            verifyTransfer();
          }
        }}
      ></ConfirmModal>
      <ConfirmModal
        acceptButtonClass="delete-button"
        show={showDeleteTransferModal}
        title="Eliminar Traspaso"
        content="¿Estás seguro que quieres eliminar el traspaso?"
        closeModal={(result: boolean) => {
          setShowTransferModal(false);
          if (result) {
            deleteTransfer();
          }
        }}
      ></ConfirmModal>
      <NoteModal editable={true} show={showNoteModal} notes={transfer.notes} closeModal={onCloseNoteModal} saveNotes={onSaveNotes} deleteNotes={() => onSaveNotes(null)} />
    </div>
  );
};

export default VerifyTransferView;
